import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { unitShapeWorkingRingAtoms } from './inventory-surface/map-atoms';
import { typeCurveUnitsVisibilityAtom } from './atoms';
import { useSurface } from './inventory-surface/surface';
import { Layer, Marker, Source, type MarkerDragEvent } from 'react-map-gl';
import {
  Layer as OrderedLayer,
  LayerBand as OrderedLayerBand,
  LAYER_ORDER,
} from 'components/map/ordered-layer';
import {
  deleteVertex,
  insertVertex,
  moveVertex,
  pieceMidpoints,
  pieceVertices,
  piecesToMultiPolygon,
  snapToRings,
  SNAP_RADIUS_M,
  type Piece,
  type Pieces,
} from 'components/geometry-editing';
import style from './tc-unit-shape-editor.module.scss';

export interface UnitShapeEdit {
  /** Controlled boundary pieces. */
  pieces: Pieces;
  /** Fires with the edited boundary after every committed change. */
  onChange: (pieces: Pieces) => void;
  /**
   * Fires immediately before a change, so a consumer keeping undo history can
   * snapshot. A whole drag gesture fires this once, on drag start.
   */
  onBeforeChange?: () => void;
  /** Pre-edit shape, drawn as a yellow dashed reference outline. */
  originalRing?: Piece | null;
  /** Snap dragged vertices to nearby targets. Defaults to true. */
  snap?: boolean;
  /**
   * The snap targets. The Unit Builder passes the true outer-ring vertices of the
   * unit and its survey tracts, followed by their quarter-grid points.
   *
   * This is the WHOLE set — nothing is read out of the map. Vertices used to also
   * snap to `land-grid` tile geometry, which is quantized to the tile grid and
   * clipped at tile edges, so a snapped vertex could never sit exactly on a real
   * survey corner; two pieces snapped near a shared edge landed on different
   * points and would not merge.
   */
  extraSnapTargets?: GeoJSON.Position[];
  /**
   * Shapes whose corners AND edges a dragged vertex may land on — the
   * development area and the survey areas picked onto it.
   *
   * Separate from `extraSnapTargets` because those are loose points (the quarter
   * grid) with no edges between them. Most of a unit's outline is edge, so
   * without these a vertex dragged against the middle of the development area's
   * boundary has nothing to snap to and lands beside it.
   */
  snapRings?: GeoJSON.Position[][];
}

/**
 * Interactive editing of a unit boundary: drag a vertex to reshape, click an
 * edge midpoint to insert one, right-click a vertex to delete it. Renders as a
 * child of `<Map>`.
 *
 * Ported from the Unit Builder mock (mock-ui
 * `inventory-management-type-curve/v2/components/EditSlotMap.tsx`). The mock
 * uses Leaflet `divIcon` markers rather than a draw library, and react-map-gl
 * `<Marker>` is the same idea — a DOM node positioned over the canvas — so the
 * handles and their behaviour carry over directly.
 *
 * @param {UnitShapeEdit} props - Controlled boundary plus change callbacks.
 * @return {JSX.Element} Boundary layers and handle markers.
 */
const TcUnitShapeEditor = ({
  pieces,
  onChange,
  onBeforeChange,
  originalRing,
  snap = true,
  extraSnapTargets,
  snapRings,
}: UnitShapeEdit) => {
  // Snap candidates are gathered once per drag rather than per frame — building
  // the set walks every target and every boundary vertex, which is far too much
  // to redo on mousemove.
  const snapTargetsRef = useRef<GeoJSON.Position[]>([]);
  const snapRingsRef = useRef<GeoJSON.Position[][]>([]);

  const vertices = useMemo(() => pieceVertices(pieces), [pieces]);
  const midpoints = useMemo(() => pieceMidpoints(vertices), [vertices]);

  const workingGeometry = useMemo(() => piecesToMultiPolygon(pieces), [pieces]);

  // Publish the largest piece so the cross-section line re-derives its frame
  // from the live shape — matching the mock, where sectionLineForUnit takes the
  // edited ring, so the line's width follows the boundary as it's dragged.
  const surface = useSurface();
  const unitsVisible = useAtomValue(typeCurveUnitsVisibilityAtom);
  const setWorkingRing = useSetAtom(unitShapeWorkingRingAtoms[surface]);
  const largestPiece = useMemo(
    () =>
      pieces.reduce<Piece | null>(
        (best, piece) => (!best || piece.length > best.length ? piece : best),
        null
      ),
    [pieces]
  );
  useEffect(() => {
    setWorkingRing(largestPiece);
    return () => setWorkingRing(null);
  }, [largestPiece, setWorkingRing]);

  const originalGeometry = useMemo<GeoJSON.LineString | null>(() => {
    if (!originalRing || originalRing.length < 3) return null;
    const [first] = originalRing;
    const last = originalRing[originalRing.length - 1];
    const closed = first[0] === last[0] && first[1] === last[1];
    return {
      type: 'LineString',
      coordinates: closed ? originalRing : [...originalRing, first],
    };
  }, [originalRing]);

  const gatherSnapTargets = useCallback(
    (dragging?: { piece: number; index: number }) => {
      if (!snap) {
        snapTargetsRef.current = [];
        return;
      }
      const unique = new Map<string, GeoJSON.Position>();
      for (const point of extraSnapTargets ?? []) {
        unique.set(`${point[0]},${point[1]}`, point);
      }

      // The boundary's own vertices, so one piece can be welded onto another:
      // drag a corner onto a neighbouring piece's corner and the two coincide
      // exactly, which is what lets them dissolve on merge. The vertex being
      // dragged is excluded — it must not snap to where it already is.
      pieces.forEach((piece, p) => {
        piece.forEach((position, i) => {
          if (dragging && dragging.piece === p && dragging.index === i) return;
          unique.set(`${position[0]},${position[1]}`, position);
        });
      });
      snapTargetsRef.current = [...unique.values()];
      snapRingsRef.current = snapRings ?? [];
    },
    [snap, extraSnapTargets, snapRings, pieces]
  );

  const handleDragStart = useCallback(
    (piece: number, index: number) => {
      gatherSnapTargets({ piece, index });
      onBeforeChange?.();
    },
    [gatherSnapTargets, onBeforeChange]
  );

  const handleDrag = useCallback(
    (p: number, i: number, event: MarkerDragEvent) => {
      const point: GeoJSON.Position = [event.lngLat.lng, event.lngLat.lat];
      const next = snap
        ? snapToRings(
            point,
            snapRingsRef.current,
            snapTargetsRef.current,
            SNAP_RADIUS_M
          )
        : point;
      const updated = moveVertex(pieces, p, i, next);
      if (updated) onChange(updated);
    },
    [pieces, onChange, snap]
  );

  const handleInsert = useCallback(
    (p: number, i: number, point: GeoJSON.Position) => {
      // Snap the new vertex as well: a midpoint added to an edge that lies on
      // the development area belongs ON it, not a few feet beside it. Targets
      // are gathered here because an insert is a click, not a drag, so
      // `handleDragStart` never ran.
      gatherSnapTargets();
      const placed = snap
        ? snapToRings(
            point,
            snapRingsRef.current,
            snapTargetsRef.current,
            SNAP_RADIUS_M
          )
        : point;
      const updated = insertVertex(pieces, p, i, placed);
      if (!updated) return;
      onBeforeChange?.();
      onChange(updated);
    },
    [pieces, onChange, onBeforeChange, snap, gatherSnapTargets]
  );

  const handleDelete = useCallback(
    (p: number, i: number, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const updated = deleteVertex(pieces, p, i);
      // Refused at three vertices — a piece can't be a polygon below that.
      if (!updated) return;
      onBeforeChange?.();
      onChange(updated);
    },
    [pieces, onChange, onBeforeChange]
  );

  // "Type Curve Units" takes the unit being edited with it: it is a type-curve
  // unit like any other, and leaving its boundary, its reference outline and its
  // drag handles on an otherwise-empty map made the checkbox look broken.
  //
  // Deliberately NOT gated on the surface. The editor only ever draws the unit
  // the user is editing, on either surface, so "don't draw type-curve units"
  // means the same thing in both places — and a surface-dependent answer here is
  // a silent way for the checkbox to stop working.
  //
  // Every hook above still runs, so the working ring stays published: the shape,
  // the cross-section frame and what Save sends are untouched. Only the drawing
  // goes.
  if (!unitsVisible) return null;

  return (
    <>
      {originalGeometry && (
        <Source
          id="tc-unit-shape-original"
          type="geojson"
          data={originalGeometry}
        >
          <Layer
            id="tc-unit-shape-original-layer"
            type="line"
            paint={{
              'line-color': '#f1c21b',
              'line-width': 2.5,
              'line-dasharray': [7, 5],
            }}
          />
        </Source>
      )}

      {/* The fill sits in its own banded source so it can drop below the
          keep-out ring and the laterals. It has to be a separate source: a
          `LayerBand` inside a `Source` would intercept the `source` prop that
          `Source` injects into its direct children. The outline stays below,
          unbanded, so it keeps reading over both. */}
      <OrderedLayerBand band={LAYER_ORDER.unitFill}>
        <Source
          id="tc-unit-shape-working-fill-source"
          type="geojson"
          data={workingGeometry}
        >
          <OrderedLayer
            id="tc-unit-shape-working-fill"
            type="fill"
            // The unit's ONE background wash, at the density picked survey
            // areas used to be drawn at (`SurveyAreasLayer`, 0.3) so the whole
            // boundary reads evenly. It was 0.05 while picked areas painted
            // their own 0.3 on top, which made the survey half of a unit look
            // like the only part with a background. The keep-out ring and the
            // laterals are solid strokes and still read over this.
            paint={{ 'fill-color': '#0f62fe', 'fill-opacity': 0.3 }}
          />
        </Source>
      </OrderedLayerBand>

      <Source id="tc-unit-shape-working" type="geojson" data={workingGeometry}>
        <Layer
          id="tc-unit-shape-working-line"
          type="line"
          paint={{ 'line-color': '#0043ce', 'line-width': 2 }}
        />
      </Source>

      {midpoints.map((mids, p) =>
        mids.map((midpoint, i) => (
          <Marker
            key={`mid-${p}-${i}`}
            longitude={midpoint[0]}
            latitude={midpoint[1]}
            anchor="center"
          >
            <div
              className={`${style.handle} ${style.handleAdd}`}
              title="Click to add a vertex"
              onClick={() => handleInsert(p, i, midpoint)}
            />
          </Marker>
        ))
      )}

      {vertices.map((verts, p) =>
        verts.map((vertex, i) => (
          <Marker
            key={`vtx-${p}-${i}`}
            longitude={vertex[0]}
            latitude={vertex[1]}
            anchor="center"
            draggable
            onDragStart={() => handleDragStart(p, i)}
            onDrag={(event) => handleDrag(p, i, event)}
          >
            <div
              className={`${style.handle} ${style.handleVertex}`}
              title="Drag to reshape · right-click to delete"
              onContextMenu={(event) => handleDelete(p, i, event)}
            />
          </Marker>
        ))
      )}
    </>
  );
};

export { TcUnitShapeEditor };
